import { useOn } from "@/lib/features";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, X } from "lucide-react";
import { imgUrl, useContent } from "@/lib/content";
import { PageLoader } from "@/pages/Home";
import { WorkStrip, useWork } from "@/components/site/OurWork";
import { GoogleReviews, ReviewForm, SiteReviews, useReviews } from "@/components/site/Reviews";

const Lightbox = ({ photos, i, setI }) => {
  useEffect(() => {
    if (i == null) return undefined;
    const key = (e) => {
      if (e.key === "Escape") setI(null);
      if (e.key === "ArrowRight") setI((i + 1) % photos.length);
      if (e.key === "ArrowLeft") setI((i - 1 + photos.length) % photos.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("keydown", key); document.body.style.overflow = ""; };
  }, [i, photos.length, setI]);
  if (i == null || !photos[i]) return null;
  return (
    <div className="lightbox" role="dialog" aria-modal="true" onClick={() => setI(null)} data-testid="work-lightbox">
      <button className="lb-btn lb-close" onClick={() => setI(null)} aria-label="Close" data-testid="work-lightbox-close"><X size={22} /></button>
      <button className="lb-btn lb-prev" onClick={(e) => { e.stopPropagation(); setI((i - 1 + photos.length) % photos.length); }} aria-label="Previous" data-testid="work-lightbox-prev"><ChevronLeft size={26} /></button>
      <img src={imgUrl(photos[i].url)} alt={photos[i].caption || "Wax Moi client result"} onClick={(e) => e.stopPropagation()} data-testid="work-lightbox-image" />
      <button className="lb-btn lb-next" onClick={(e) => { e.stopPropagation(); setI((i + 1) % photos.length); }} aria-label="Next" data-testid="work-lightbox-next"><ChevronRight size={26} /></button>
      <span className="lb-count">{i + 1} / {photos.length}{photos[i].caption && ` · ${photos[i].caption}`}</span>
    </div>
  );
};

export default function OurWorkPage() {
  const c = useContent().data;
  const { data: photos } = useWork();
  const { data: rv } = useReviews();
  const reviewsOn = useOn("reviews");
  const [params] = useSearchParams();
  const [open, setOpen] = useState(null);
  useEffect(() => { const p = params.get("photo"); if (p !== null && photos?.length) setOpen(Number(p) % photos.length); }, [params, photos]);
  if (!c || !photos) return <PageLoader />;
  const w = c.work;
  return (
    <div className="wm work-page" data-testid="work-page">
      <header className="look-top">
        <Link to="/" className="look-back" data-testid="work-back-link"><ArrowLeft size={18} /> Home</Link>
        <Link to="/" className="logo logo-black"><img src="/brand/logo-black.png" alt="Wax Moi Day Spa" width="835" height="240" /></Link>
      </header>
      <section className="shop-hero wrap">
        <p className="eyebrow">{w.eyebrow}</p>
        <h1>{w.page_title}</h1>
        <p>{w.page_text}</p>
      </section>
      {photos.length > 0 && <WorkStrip photos={photos} onOpen={setOpen} testId="work-page-strip" />}
      <div className="work-grid wrap" data-testid="work-grid">
        {photos.map((p, i) => (
          <button type="button" key={p.id} className="work-cell" onClick={() => setOpen(i)} data-testid={`work-grid-photo-${i}`}>
            <img src={imgUrl(p.url)} alt={p.caption || "Wax Moi client result"} loading="lazy" decoding="async" />
          </button>
        ))}
      </div>
      {reviewsOn && <section className="section reviews wrap" id="reviews" data-testid="reviews-section">
        <p className="eyebrow">Reviews</p>
        <h2 className="team-title">{w.reviews_title}</h2>
        {rv && <GoogleReviews g={rv.google} url={rv.review_url} />}
        {rv && <SiteReviews list={rv.site} avg={rv.site_average} count={rv.site_count} />}
        <ReviewForm />
      </section>}
      <Lightbox photos={photos} i={open} setI={setOpen} />
    </div>
  );
}
